import { Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { useQuery } from "@tanstack/react-query";
import { fetchBlog, fetchCharacterCards, characterField } from "@/lib/shopify";

type TeaserHref =
  | { to: "/field-notes" }
  | { to: "/field-notes/$blog"; params: { blog: string } }
  | { to: "/field-notes/$blog/$article"; params: { blog: string; article: string } }
  | { to: "/character-archive" }
  | { to: "/character-archive/$slug"; params: { slug: string } }
  | { external: string };

type TeaserCard = {
  category: string;
  title: string;
  excerpt: string;
  numeral: string;
  href: TeaserHref;
};

const SECTIONS = [
  { label: "Natural Perfumery Primer", blog: "the-herbal-codex", numeral: "№ I" },
  { label: "Bottle Mythologies", blog: "perfume-chapters", numeral: "№ II" },
] as const;

const PLACEHOLDERS: TeaserCard[] = [
  {
    category: "Natural Perfumery Primer",
    title: "How Smoke Becomes Soft",
    excerpt:
      "Incense, charred wood, pale resins, and the difficulty of making fire behave.",
    numeral: "№ I",
    href: { to: "/field-notes/$blog", params: { blog: "the-herbal-codex" } },
  },
  {
    category: "Materia Botanica",
    title: "Lavender, the Well-Behaved Heretic",
    excerpt:
      "Clean linen, cut stone, and a field pretending it has never known ghosts.",
    numeral: "№ II",
    href: { to: "/field-notes/$blog", params: { blog: "materia-botanica" } },
  },
  {
    category: "Bottle Mythologies",
    title: "Inside The Archivist",
    excerpt:
      "Dust, ink, wine-soaked oak, and the warmth of a sealed drawer.",
    numeral: "№ III",
    href: { to: "/field-notes/$blog", params: { blog: "perfume-chapters" } },
  },
  {
    category: "Character Archive",
    title: "Notes as Personas",
    excerpt:
      "A cabinet of botanical characters: saints, tricksters, ghosts, and minor villains in aromatic form.",
    numeral: "№ IV",
    href: { to: "/character-archive" },
  },
];

function FaintSigil() {
  return (
    <svg
      aria-hidden
      width="64"
      height="64"
      viewBox="0 0 64 64"
      fill="none"
      stroke="currentColor"
      strokeWidth="0.6"
      className="pointer-events-none absolute bottom-4 right-4 text-gold/30"
    >
      <circle cx="32" cy="32" r="22" />
      <path d="M32 10v44M10 32h44" />
      <path d="M18 18l28 28M46 18L18 46" />
    </svg>
  );
}

function renderLink(href: TeaserHref, className: string, children: React.ReactNode) {
  if ("external" in href) {
    return (
      <a
        href={href.external}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }
  switch (href.to) {
    case "/field-notes":
      return <Link to="/field-notes" className={className}>{children}</Link>;
    case "/field-notes/$blog":
      return <Link to="/field-notes/$blog" params={href.params} className={className}>{children}</Link>;
    case "/field-notes/$blog/$article":
      return <Link to="/field-notes/$blog/$article" params={href.params} className={className}>{children}</Link>;
    case "/character-archive":
      return <Link to="/character-archive" className={className}>{children}</Link>;
    case "/character-archive/$slug":
      return <Link to="/character-archive/$slug" params={href.params} className={className}>{children}</Link>;
  }
}

const EXCERPT_MAX = 160;

function trimExcerpt(text: string | null | undefined, fallback: string): string {
  const raw = (text ?? "").replace(/\s+/g, " ").trim();
  if (!raw) return fallback;
  if (raw.length <= EXCERPT_MAX) return raw;
  const slice = raw.slice(0, EXCERPT_MAX);
  const lastSpace = slice.lastIndexOf(" ");
  const cut = lastSpace > 80 ? slice.slice(0, lastSpace) : slice;
  return cut.replace(/[,;:\-–—\s]+$/, "") + "…";
}

function trimTitle(text: string): string {
  // Collapse overly long apothecary-style titles like
  // "Apothecary Profile: Marshmallow (Althaea officinalis)" → "Apothecary Profile: Marshmallow"
  return text.replace(/\s*\([^)]*\)\s*$/, "").trim();
}

export function FieldNotesTeaser() {
  const blogQueries = SECTIONS.map((s) =>
    useQuery({
      queryKey: ["shopify-blog", s.blog, 1],
      queryFn: () => fetchBlog(s.blog, 1),
      staleTime: 5 * 60 * 1000,
    }),
  );

  const characterQuery = useQuery({
    queryKey: ["shopify-character-cards", 2],
    queryFn: () => fetchCharacterCards(2),
    staleTime: 5 * 60 * 1000,
  });

  const cards: TeaserCard[] = SECTIONS.map((section, i) => {
    const edge = (blogQueries[i].data?.articles?.edges as
      | Array<{ node: { handle: string; title: string; excerpt: string | null } }>
      | undefined)?.[0];
    if (edge) {
      const cleanedTitle = trimTitle(
        edge.node.title
          .replace(/\s+in\s+Perfume\??$/i, "?")
          .replace(/\?\?$/, "?"),
      );
      return {
        category: section.label,
        title: cleanedTitle,
        excerpt: trimExcerpt(edge.node.excerpt, PLACEHOLDERS[i].excerpt),
        numeral: section.numeral,
        href: {
          to: "/field-notes/$blog/$article" as const,
          params: { blog: section.blog, article: edge.node.handle },
        },
      };
    }
    return PLACEHOLDERS[i];
  });

  const characterNumerals = ["№ III", "№ IV"];
  const characterCards = characterQuery.data ?? [];
  for (let idx = 0; idx < 2; idx++) {
    const characterCard = characterCards[idx];
    if (characterCard) {
      const handle = characterCard.handle;
      const name = characterField(characterCard, "name") ?? characterField(characterCard, "title") ?? "Character Archive";
      const tagline =
        characterField(characterCard, "tagline") ??
        characterField(characterCard, "subtitle") ??
        characterField(characterCard, "summary") ??
        null;
      const link = characterField(characterCard, "link");
      cards.push({
        category: "Character Archive",
        title: trimTitle(name),
        excerpt: trimExcerpt(tagline, PLACEHOLDERS[3].excerpt),
        numeral: characterNumerals[idx],
        href: link ? { external: link } : { to: "/character-archive/$slug", params: { slug: handle } },
      });
    } else {
      cards.push({ ...PLACEHOLDERS[3], numeral: characterNumerals[idx] });
    }
  }

  return (
    <section className="relative py-20 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-2xl">
          <p className="eyebrow">From the Archives</p>
          <p className="mt-5 font-serif text-xl leading-relaxed italic text-muted-foreground sm:text-2xl">
            Ingredient lore, bottle mythologies, and botanical portraits from
            the stranger end of natural perfumery.
          </p>
        </div>

        <ul className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((card, i) => (
            <motion.li
              key={`${card.category}-${card.title}-${i}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="group relative flex flex-col overflow-hidden border border-border bg-card/40 p-8 backdrop-blur-sm transition-colors hover:border-gold/40"
            >
              <span
                aria-hidden
                className="absolute right-5 top-5 font-display text-[0.6rem] uppercase tracking-[0.28em] text-muted-foreground/60"
              >
                {card.numeral}
              </span>

              <p className="font-display text-[0.72rem] uppercase tracking-[0.26em] text-gold/80">
                {card.category}
              </p>
              <h3 className="mt-4 line-clamp-2 min-h-[3.5rem] font-display text-xl leading-snug text-parchment">
                {renderLink(card.href, "transition-colors hover:text-gold-soft", card.title)}
              </h3>
              <p className="mt-4 hidden line-clamp-5 font-serif text-base italic leading-relaxed text-muted-foreground md:block">
                {card.excerpt}
              </p>

              <div className="mt-8">
                {renderLink(
                  card.href,
                  "relative z-10 inline-block border-b border-gold/40 pb-1 font-display text-[0.65rem] uppercase tracking-[0.28em] text-gold transition-colors hover:border-gold hover:text-gold-soft",
                  <>Read note →</>,
                )}
              </div>

              <FaintSigil />
            </motion.li>
          ))}
        </ul>

        <div className="mt-14">
          <Link
            to="/field-notes"
            className="inline-block border-b border-gold/50 pb-1 font-display text-xs uppercase tracking-[0.28em] text-gold transition-colors hover:border-gold hover:text-gold-soft"
          >
            Explore the Field Notes →
          </Link>
        </div>
      </div>
    </section>
  );
}
